import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { appointmentService } from '../services/AppointmentService';
import { clientService } from '../services/ClientService';
import { userService } from '../services/UserService';
import { sessionService } from '../services/SessionService';
import { travelService } from '../services/TravelService';
import { useBeginSession } from '../hooks/useBeginSession';
import { seedMySessions } from '../utils/seedMySessions';
import { generateAndStoreSessionPdf } from '../services/SessionPdfService';
import { getCurrentPosition } from '../utils/geolocation';
import { Timestamp } from 'firebase/firestore';
import { Appointment } from '../models/Appointment';
import { TravelTrip, TravelLegType } from '../models/TravelTrip';
import {
  Session,
  SessionType,
  SESSION_TYPE_LABELS,
  COMPLETED_STATUSES,
} from '../models/Session';
import {
  MapPin,
  Play,
  Square,
  Mic,
  FileText,
  CalendarClock,
  AlertTriangle,
  CheckCircle2,
  Hourglass,
  Download,
  Navigation,
  XCircle,
} from 'lucide-react';

const DEFAULT_DEADLINE_HOURS = 24;
const COMPLETED_PAGE_SIZE = 10;
const COMPLETED_WINDOW_DAYS = 30;

/* ----------------------------- time helpers ----------------------------- */

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};
const endOfToday = () => {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
};

const fmtTime = (d: Date) =>
  d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

const fmtTimeRange = (start: Date, end: Date) => `${fmtTime(start)} – ${fmtTime(end)}`;

const fmtDateTime = (d: Date) =>
  d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' }) +
  ' · ' +
  fmtTime(d);

// Full timestamp for the audit trail, e.g. "Jul 29, 2026 3:45 PM"
const fmtStamp = (d: Date) =>
  d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) + ' ' + fmtTime(d);

// Live count-up "H:MM:SS"
const fmtElapsed = (ms: number) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${h}:${pad(m)}:${pad(s)}`;
};

// Coarse "2h 15m" / "45m" for durations
const fmtDuration = (ms: number) => {
  const mins = Math.max(0, Math.round(ms / 60000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

interface DeadlineVisual {
  overdue: boolean;
  label: string; // e.g. "5h 23m left" or "OVERDUE"
  color: string; // css var
  bg: string;
}

const deadlineVisual = (
  deadline: Date | null,
  now: Date,
  totalWindowMs: number
): DeadlineVisual => {
  if (!deadline) {
    return {
      overdue: false,
      label: 'No deadline',
      color: 'var(--color-gray-500)',
      bg: 'var(--color-gray-100)',
    };
  }
  const remaining = deadline.getTime() - now.getTime();
  if (remaining <= 0) {
    return {
      overdue: true,
      label: 'OVERDUE',
      color: 'var(--color-white)',
      bg: 'var(--color-error)',
    };
  }
  const pct = totalWindowMs > 0 ? remaining / totalWindowMs : 1;
  const label = `${fmtDuration(remaining)} left`;
  if (pct > 0.5) {
    return { overdue: false, label, color: 'var(--color-success)', bg: 'rgba(16,185,129,0.12)' };
  }
  if (pct >= 0.25) {
    return { overdue: false, label, color: 'var(--color-accent-dark)', bg: 'rgba(196,154,42,0.15)' };
  }
  return { overdue: false, label, color: 'var(--color-error)', bg: 'rgba(239,68,68,0.12)' };
};

const sessionTypeLabel = (t: SessionType) => SESSION_TYPE_LABELS[t] || t;

/* ------------------------------ shared UI ------------------------------- */

const SectionHeader: React.FC<{ title: string; count?: number; icon: React.ElementType }> = ({
  title,
  count,
  icon: Icon,
}) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-4)' }}>
    <Icon size={20} style={{ color: 'var(--color-primary)' }} />
    <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-bold)', color: 'var(--color-text-primary)' }}>
      {title}
    </h2>
    {typeof count === 'number' && count > 0 && (
      <span
        style={{
          fontSize: 'var(--text-xs)',
          fontWeight: 'var(--font-semibold)',
          color: 'var(--color-white)',
          backgroundColor: 'var(--color-primary)',
          borderRadius: '999px',
          padding: '2px 8px',
        }}
      >
        {count}
      </span>
    )}
  </div>
);

const TypePill: React.FC<{ type: SessionType }> = ({ type }) => (
  <span
    style={{
      fontSize: 'var(--text-xs)',
      fontWeight: 'var(--font-medium)',
      color: 'var(--color-primary)',
      backgroundColor: 'var(--color-light-accent)',
      borderRadius: 'var(--radius-md)',
      padding: '2px 10px',
      whiteSpace: 'nowrap',
    }}
  >
    {sessionTypeLabel(type)}
  </span>
);

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-white)',
  borderRadius: 'var(--radius-xl)',
  boxShadow: 'var(--shadow-md)',
  border: '1px solid var(--color-border)',
  padding: 'var(--spacing-5)',
};

const btnPrimary: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--spacing-2)',
  padding: 'var(--spacing-3) var(--spacing-5)',
  backgroundColor: 'var(--color-primary)',
  color: 'var(--color-white)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  fontWeight: 'var(--font-semibold)',
  cursor: 'pointer',
};

const btnGhost: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--spacing-2)',
  padding: 'var(--spacing-3) var(--spacing-4)',
  backgroundColor: 'transparent',
  color: 'var(--color-primary)',
  border: '1px solid var(--color-primary)',
  borderRadius: 'var(--radius-md)',
  fontWeight: 'var(--font-medium)',
  cursor: 'pointer',
};

const metaRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-2)',
  fontSize: 'var(--text-sm)',
  color: 'var(--color-text-secondary)',
};

const emptyState = (text: string) => (
  <div
    style={{
      ...cardStyle,
      textAlign: 'center',
      color: 'var(--color-text-secondary)',
      fontSize: 'var(--text-sm)',
      padding: 'var(--spacing-8)',
    }}
  >
    {text}
  </div>
);

/* ------------------------- status badge (Section 4) --------------------- */

const STATUS_BADGE: Record<string, { label: string; color: string; bg: string }> = {
  submitted: { label: 'Pending Review', color: 'var(--color-accent-dark)', bg: 'rgba(196,154,42,0.15)' },
  approved: { label: 'Approved', color: '#166534', bg: 'rgba(16,185,129,0.15)' },
  'needs-revision': { label: 'Needs Revision', color: '#991B1B', bg: 'rgba(239,68,68,0.12)' },
  revised: { label: 'Revised & Resubmitted', color: '#1E40AF', bg: 'rgba(59,130,246,0.12)' },
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const b = STATUS_BADGE[status];
  if (!b) return null;
  return (
    <span
      style={{
        fontSize: 'var(--text-xs)',
        fontWeight: 'var(--font-semibold)',
        color: b.color,
        backgroundColor: b.bg,
        borderRadius: '999px',
        padding: '3px 10px',
        whiteSpace: 'nowrap',
      }}
    >
      {b.label}
    </span>
  );
};

/* -------------------------------- page ---------------------------------- */

export const MySessions: React.FC = () => {
  const { user } = useAuth();
  const { company } = useCompany();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState<Date>(new Date());

  const [todayAppointments, setTodayAppointments] = useState<Array<Appointment & { clientName: string }>>([]);
  const [supervisorNames, setSupervisorNames] = useState<Map<string, string>>(new Map());
  const [sessions, setSessions] = useState<Session[]>([]);
  const [completedShown, setCompletedShown] = useState(COMPLETED_PAGE_SIZE);
  const [seeding, setSeeding] = useState(false);
  const [pdfBusyId, setPdfBusyId] = useState<string | null>(null);

  // Travel capture ("On my way"). The RBT can have at most one trip en route.
  const [activeTrip, setActiveTrip] = useState<TravelTrip | null>(null);
  const [tripBusyId, setTripBusyId] = useState<string | null>(null);
  const [tripError, setTripError] = useState<string | null>(null);

  const deadlineHours = company?.settings?.noteSubmissionDeadlineHours ?? DEFAULT_DEADLINE_HOURS;
  const totalWindowMs = deadlineHours * 60 * 60 * 1000;
  const isDev = process.env.NODE_ENV !== 'production';

  // 1-second tick drives live timers (elapsed + countdowns)
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // Load today's appointments + name lookups (one-time / on demand).
  const loadAppointments = useCallback(async () => {
    if (!user) return;
    try {
      const [appts, clients, users] = await Promise.all([
        appointmentService.getAppointmentsByRbt(user.id),
        clientService.getClientsByCompany(user.companyId),
        userService.getUsersByCompany(user.companyId),
      ]);

      const clientNameById = new Map(
        clients.map((c) => [c.id, `${c.firstName} ${c.lastName}`.trim()])
      );
      setSupervisorNames(new Map(users.map((u) => [u.id, u.name])));

      const from = startOfToday().getTime();
      const to = endOfToday().getTime();
      const todays = appts
        .filter((a) => {
          const t = a.startTime.getTime();
          return t >= from && t <= to && a.status !== 'cancelled' && a.status !== 'completed' && a.status !== 'no-show';
        })
        .sort((a, b) => a.startTime.getTime() - b.startTime.getTime())
        .map((a) => ({ ...a, clientName: clientNameById.get(a.clientId) || 'Client' }));

      setTodayAppointments(todays);

      // Load any in-flight travel trip ("On my way" tapped, not yet arrived).
      try {
        setActiveTrip(await travelService.getActiveTrip(user.id));
      } catch (tripErr) {
        console.error('MySessions active-trip load error:', tripErr);
      }
    } catch (e: any) {
      console.error('MySessions appointments load error:', e);
      setError('Could not load your appointments. Please try again.');
    }
  }, [user]);

  useEffect(() => {
    loadAppointments().finally(() => setLoading(false));
  }, [loadAppointments]);

  // Real-time subscription to this RBT's session documents.
  useEffect(() => {
    if (!user) return;
    const unsubscribe = sessionService.subscribeSessionsByRbt(
      user.companyId,
      user.id,
      (live) => setSessions(live),
      () => setError('Live updates unavailable (check Firestore access).')
    );
    return () => unsubscribe();
  }, [user]);

  // Sessions that already exist for an appointment (so we don't show it twice)
  const startedAppointmentIds = new Set(
    sessions.filter((s) => s.status !== 'scheduled' && s.appointmentId).map((s) => s.appointmentId as string)
  );
  const upcoming = todayAppointments.filter((a) => !startedAppointmentIds.has(a.id));

  // Derive the four sections from the session list
  const activeSession = sessions.find((s) => s.status === 'in-progress') || null;

  const pending = sessions
    .filter((s) => s.status === 'pending-notes')
    .sort((a, b) => {
      const da = a.deadline?.getTime() ?? Infinity;
      const db = b.deadline?.getTime() ?? Infinity;
      return da - db; // closest deadline first
    });

  const completedCutoff = now.getTime() - COMPLETED_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  const completed = sessions
    .filter((s) => COMPLETED_STATUSES.includes(s.status))
    .filter((s) => {
      const ref = (s.submittedAt || s.actualEnd || s.updatedAt).getTime();
      return ref >= completedCutoff;
    })
    .sort((a, b) => {
      const ra = (a.submittedAt || a.actualEnd || a.updatedAt).getTime();
      const rb = (b.submittedAt || b.actualEnd || b.updatedAt).getTime();
      return rb - ra; // newest first
    });

  // Start Session — the shared entry-point action (Phase 3): findOrCreate the
  // canonical session (idempotent) + navigate. Same action as the Dashboard.
  const beginSession = useBeginSession();

  // "On my way to [client]" — capture the TRUE trip origin (live GPS) and open a
  // travel trip tied to this appointment. Arrival is folded into the session
  // check-in ("I'm here") on the Start Session flow, so the RBT doesn't tap twice.
  const commutePolicy = {
    reimburseFirstLeg: company?.settings?.mileageReimburseFirstLeg,
    reimburseLastLeg: company?.settings?.mileageReimburseLastLeg,
  };

  const handleOnMyWay = async (a: Appointment & { clientName: string }) => {
    if (!user) return;
    setTripBusyId(a.id);
    setTripError(null);
    try {
      const position = await getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      });
      // todayAppointments is sorted by startTime asc — the first is the day's
      // home-to-first leg (commute by default); later ones are between-clients.
      const firstApptId = todayAppointments[0]?.id;
      const legType: TravelLegType = a.id === firstApptId ? 'home-to-first' : 'between-clients';
      await travelService.startTrip(
        {
          companyId: user.companyId,
          rbtId: user.id,
          rbtName: user.name,
          appointmentId: a.id,
          clientId: a.clientId,
          clientName: a.clientName,
          origin: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: Timestamp.now(),
          },
          legType,
        },
        commutePolicy
      );
      setActiveTrip(await travelService.getActiveTrip(user.id));
    } catch (e: any) {
      console.error('On my way error:', e);
      setTripError(
        e?.message || 'Could not capture your location. Enable location services and try again.'
      );
    } finally {
      setTripBusyId(null);
    }
  };

  const handleCancelTrip = async () => {
    if (!activeTrip) return;
    setTripBusyId(activeTrip.appointmentId);
    try {
      await travelService.cancelTrip(activeTrip.id);
      setActiveTrip(null);
    } catch (e) {
      console.error('cancel trip error:', e);
    } finally {
      setTripBusyId(null);
    }
  };

  // End: mark the active session pending-notes with a deadline. The
  // real-time listener moves it into Pending Reports; we stay on the page.
  const endSession = async () => {
    if (!user || !activeSession) return;
    try {
      await sessionService.endSession(user.companyId, activeSession.id, deadlineHours);
    } catch (e) {
      console.error('endSession error:', e);
    }
  };

  const downloadPdf = async (s: Session) => {
    if (!user) return;
    setPdfBusyId(s.id);
    try {
      const url = await generateAndStoreSessionPdf(s, company);
      await sessionService.saveNotePdfUrl(user.companyId, s.id, url);
    } catch (e) {
      console.error('PDF export error:', e);
    } finally {
      setPdfBusyId(null);
    }
  };

  const handleSeed = async () => {
    if (!user) return;
    setSeeding(true);
    try {
      const firstClient = todayAppointments[0];
      await seedMySessions({
        companyId: user.companyId,
        rbtId: user.id,
        rbtName: user.name,
        clientId: firstClient?.clientId,
        clientName: firstClient?.clientName,
      });
      await loadAppointments(); // sessions arrive via the live listener
    } catch (e) {
      console.error('seed error:', e);
    } finally {
      setSeeding(false);
    }
  };

  const sectionGap = { marginBottom: 'var(--spacing-10)' };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--spacing-12)' }}>
        <div style={{ color: 'var(--color-text-secondary)' }}>Loading your sessions…</div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      {isDev && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--spacing-3)',
            flexWrap: 'wrap',
            padding: 'var(--spacing-3) var(--spacing-4)',
            marginBottom: 'var(--spacing-6)',
            border: '1px dashed var(--color-gray-400)',
            borderRadius: 'var(--radius-md)',
            backgroundColor: 'var(--color-gray-50)',
          }}
        >
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
            Dev tools — creates one sample record in each state (not shown in production)
          </span>
          <button
            style={{ ...btnGhost, opacity: seeding ? 0.6 : 1 }}
            onClick={handleSeed}
            disabled={seeding}
          >
            {seeding ? 'Creating…' : 'Create Test Sessions'}
          </button>
        </div>
      )}
      {error && (
        <div
          style={{
            ...cardStyle,
            marginBottom: 'var(--spacing-6)',
            borderColor: 'var(--color-error)',
            color: 'var(--color-error)',
          }}
        >
          {error}
        </div>
      )}

      {/* ---------------- SECTION 1: UPCOMING ---------------- */}
      <section style={sectionGap}>
        <SectionHeader title="Upcoming Sessions" count={upcoming.length} icon={CalendarClock} />
        {tripError && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-2)',
              padding: 'var(--spacing-3) var(--spacing-4)',
              marginBottom: 'var(--spacing-4)',
              borderRadius: 'var(--radius-md)',
              backgroundColor: 'rgba(239,68,68,0.12)',
              color: 'var(--color-error)',
              fontSize: 'var(--text-sm)',
            }}
          >
            <AlertTriangle size={16} />
            {tripError}
          </div>
        )}
        {upcoming.length === 0 ? (
          emptyState('No sessions scheduled for today')
        ) : (
          <div style={{ display: 'grid', gap: 'var(--spacing-4)' }}>
            {upcoming.map((a) => {
              const type: SessionType = a.supervisionRequired ? 'supervision' : 'direct-therapy';
              const address = [a.location?.address, a.location?.city].filter(Boolean).join(', ');
              return (
                <div key={a.id} style={cardStyle}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 'var(--spacing-4)',
                      flexWrap: 'wrap',
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--spacing-3)',
                          marginBottom: 'var(--spacing-2)',
                          flexWrap: 'wrap',
                        }}
                      >
                        <span style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)', color: 'var(--color-text-primary)' }}>
                          {a.clientName}
                        </span>
                        <TypePill type={type} />
                      </div>
                      <div style={{ ...metaRow, marginBottom: '4px' }}>
                        <CalendarClock size={15} />
                        {fmtTimeRange(a.startTime, a.endTime)}
                      </div>
                      {address && (
                        <div style={metaRow}>
                          <MapPin size={15} />
                          {address}
                        </div>
                      )}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: 'var(--spacing-2)',
                      }}
                    >
                      {activeTrip && activeTrip.appointmentId === a.id ? (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: 'var(--text-xs)',
                            fontWeight: 'var(--font-semibold)',
                            color: 'var(--color-primary)',
                            backgroundColor: 'var(--color-light-accent)',
                            borderRadius: '999px',
                            padding: '3px 10px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <Navigation size={13} /> En route — Start Session, then "I'm Here" to arrive
                        </span>
                      ) : !activeTrip ? (
                        <button
                          style={{ ...btnGhost, opacity: tripBusyId === a.id ? 0.6 : 1 }}
                          onClick={() => handleOnMyWay(a)}
                          disabled={tripBusyId === a.id}
                        >
                          <Navigation size={16} />
                          {tripBusyId === a.id ? 'Capturing…' : 'On my way'}
                        </button>
                      ) : null}
                      <button
                        style={btnPrimary}
                        onClick={() =>
                          beginSession(a, {
                            clientName: a.clientName,
                            supervisorName: supervisorNames.get(a.supervisorId) || '',
                          })
                        }
                      >
                        <Play size={16} />
                        Start Session
                      </button>
                      {activeTrip && activeTrip.appointmentId === a.id && (
                        <button
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: 'var(--text-xs)',
                            color: 'var(--color-text-secondary)',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0,
                          }}
                          onClick={handleCancelTrip}
                          disabled={tripBusyId === a.id}
                        >
                          <XCircle size={13} /> Cancel trip
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ---------------- SECTION 2: IN PROGRESS ---------------- */}
      {activeSession && (
        <section style={sectionGap}>
          <SectionHeader title="In Progress" icon={Hourglass} />
          <div
            style={{
              ...cardStyle,
              borderLeft: '4px solid var(--color-primary)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 'var(--spacing-4)',
              flexWrap: 'wrap',
            }}
          >
            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-3)',
                  marginBottom: 'var(--spacing-2)',
                  flexWrap: 'wrap',
                }}
              >
                <span style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)', color: 'var(--color-text-primary)' }}>
                  {activeSession.clientName}
                </span>
                <TypePill type={activeSession.sessionType} />
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: 'var(--text-xs)',
                    fontWeight: 'var(--font-semibold)',
                    color: 'var(--color-white)',
                    backgroundColor: 'var(--color-error)',
                    borderRadius: '999px',
                    padding: '2px 10px',
                  }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: 'var(--color-white)',
                      display: 'inline-block',
                    }}
                  />
                  LIVE
                </span>
              </div>
              <div
                style={{
                  fontSize: 'var(--text-3xl)',
                  fontWeight: 'var(--font-bold)',
                  color: 'var(--color-primary)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {activeSession.actualStart
                  ? fmtElapsed(now.getTime() - activeSession.actualStart.getTime())
                  : '0:00:00'}
              </div>
            </div>
            <button
              style={{ ...btnPrimary, backgroundColor: 'var(--color-error)' }}
              onClick={endSession}
            >
              <Square size={16} />
              End Session
            </button>
          </div>
        </section>
      )}

      {/* ---------------- SECTION 3: PENDING REPORTS ---------------- */}
      <section style={sectionGap}>
        <SectionHeader title="Pending Reports" count={pending.length} icon={AlertTriangle} />
        {pending.length === 0 ? (
          emptyState('No reports awaiting submission')
        ) : (
          <div style={{ display: 'grid', gap: 'var(--spacing-4)' }}>
            {pending.map((s) => {
              const dv = deadlineVisual(s.deadline, now, totalWindowMs);
              const endedAt = s.actualEnd;
              return (
                <div key={s.id} style={cardStyle}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 'var(--spacing-4)',
                      flexWrap: 'wrap',
                      marginBottom: 'var(--spacing-4)',
                    }}
                  >
                    <div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--spacing-3)',
                          marginBottom: 'var(--spacing-2)',
                          flexWrap: 'wrap',
                        }}
                      >
                        <span style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)', color: 'var(--color-text-primary)' }}>
                          {s.clientName}
                        </span>
                        <TypePill type={s.sessionType} />
                      </div>
                      <div style={{ ...metaRow, marginBottom: '4px' }}>
                        <CalendarClock size={15} />
                        {fmtDateTime(s.scheduledStart)}
                      </div>
                      {endedAt && (
                        <div style={metaRow}>
                          Ended {fmtDuration(now.getTime() - endedAt.getTime())} ago
                        </div>
                      )}
                    </div>
                    {/* Countdown timer */}
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-bold)',
                        color: dv.color,
                        backgroundColor: dv.bg,
                        borderRadius: 'var(--radius-md)',
                        padding: '6px 12px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {dv.overdue ? <AlertTriangle size={15} /> : <Hourglass size={15} />}
                      {dv.label}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 'var(--spacing-3)', flexWrap: 'wrap' }}>
                    <button style={btnPrimary} onClick={() => navigate(`/my-sessions/${s.id}/dictate`)}>
                      <Mic size={16} />
                      Dictate Notes
                    </button>
                    <button style={btnGhost} onClick={() => navigate(`/my-sessions/${s.id}/notes`)}>
                      <FileText size={16} />
                      Write Notes
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ---------------- SECTION 4: COMPLETED ---------------- */}
      <section style={sectionGap}>
        <SectionHeader title="Completed" count={completed.length} icon={CheckCircle2} />
        {completed.length === 0 ? (
          emptyState('No completed sessions in the last 30 days')
        ) : (
          <>
            <div style={{ display: 'grid', gap: 'var(--spacing-4)' }}>
              {completed.slice(0, completedShown).map((s) => {
                const when = s.submittedAt || s.actualEnd || s.updatedAt;
                const needsRevision = s.status === 'needs-revision';
                return (
                  <div
                    key={s.id}
                    style={{ ...cardStyle, cursor: 'pointer' }}
                    onClick={() => navigate(`/my-sessions/${s.id}/notes`, { state: { mode: 'view' } })}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') navigate(`/my-sessions/${s.id}/notes`, { state: { mode: 'view' } });
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 'var(--spacing-4)',
                        flexWrap: 'wrap',
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)', color: 'var(--color-text-primary)', marginBottom: 'var(--spacing-1)' }}>
                          {s.clientName}
                        </div>
                        <div style={{ ...metaRow, marginBottom: '4px' }}>
                          <CalendarClock size={15} />
                          {fmtDateTime(when)}
                        </div>
                        {/* Audit trail timestamps */}
                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
                          {s.submittedAt && `Submitted: ${fmtStamp(s.submittedAt)}`}
                          {s.submittedAt && s.approvedAt && '  |  '}
                          {s.approvedAt && `Approved: ${fmtStamp(s.approvedAt)}`}
                        </div>
                      </div>
                      <StatusBadge status={s.status} />
                    </div>

                    {s.status === 'approved' && (
                      <div style={{ marginTop: 'var(--spacing-3)' }}>
                        <button
                          style={{ ...btnGhost, opacity: pdfBusyId === s.id ? 0.6 : 1 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadPdf(s);
                          }}
                          disabled={pdfBusyId === s.id}
                        >
                          <Download size={16} />
                          {pdfBusyId === s.id ? 'Generating…' : 'Download PDF'}
                        </button>
                      </div>
                    )}

                    {needsRevision && (
                      <div
                        style={{
                          marginTop: 'var(--spacing-4)',
                          padding: 'var(--spacing-3)',
                          backgroundColor: 'rgba(239,68,68,0.06)',
                          border: '1px solid rgba(239,68,68,0.2)',
                          borderRadius: 'var(--radius-md)',
                        }}
                      >
                        <div style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-semibold)', color: '#991B1B', marginBottom: '4px' }}>
                          BCBA comments
                        </div>
                        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-3)' }}>
                          {s.supervisorComments || 'Revision requested.'}
                        </div>
                        <button
                          style={btnPrimary}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/my-sessions/${s.id}/notes`, { state: { mode: 'edit' } });
                          }}
                        >
                          <FileText size={16} />
                          Edit &amp; Resubmit
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {completedShown < completed.length && (
              <div style={{ textAlign: 'center', marginTop: 'var(--spacing-5)' }}>
                <button
                  style={btnGhost}
                  onClick={() => setCompletedShown((n) => n + COMPLETED_PAGE_SIZE)}
                >
                  Load More ({completed.length - completedShown} more)
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default MySessions;
